/**
 * Browser-safe SDK v1 HTML policy shared by block and field renderers.
 *
 * This sanitizer is a defense-in-depth output policy. Production isolation,
 * timeout, payload, and worker controls remain the responsibility of the host.
 */
export const HTML_POLICY_MAX_OUTPUT_CHARACTERS = 250000;
export const HTML_POLICY_ALLOWED_TAGS = [
    'p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'blockquote',
    'code', 'pre', 'a', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img',
    'article', 'div', 'section', 'svg', 'circle', 'line', 'path', 'polyline', 'rect',
];
export const HTML_POLICY_ALLOWED_ATTRIBUTES = [
    'href', 'src', 'alt', 'title', 'target', 'rel', 'width', 'height', 'loading',
    'aria-hidden', 'aria-label', 'aria-labelledby', 'class', 'cx', 'cy', 'd',
    'data-emporion-icon', 'data-renderer', 'fill', 'focusable', 'id', 'points',
    'r', 'role', 'rx', 'ry', 'stroke', 'stroke-linecap', 'stroke-linejoin',
    'stroke-width', 'style', 'viewBox', 'x', 'x1', 'x2', 'xmlns', 'y', 'y1', 'y2',
];
export const HTML_POLICY_ALLOWED_STYLE_PROPERTIES = [
    'align-items', 'background', 'border', 'border-radius', 'box-shadow', 'color',
    'display', 'font-size', 'font-weight', 'flex', 'flex-basis', 'flex-wrap',
    'flex-shrink', 'gap', 'grid-template-columns', 'height', 'justify-content',
    'letter-spacing', 'line-height', 'margin', 'margin-bottom', 'max-width',
    'min-width', 'min-height', 'object-fit', 'overflow', 'overflow-x', 'padding',
    'padding-bottom', 'scroll-snap-align', 'scroll-snap-type', 'text-align', 'width',
];
export const HTML_POLICY_FORBIDDEN_TAGS = [
    'script', 'iframe', 'object', 'embed', 'meta', 'base', 'link', 'form',
    'input', 'button', 'textarea', 'select', 'option',
];
const VOID_TAGS = new Set(['br', 'img']);
const FORBIDDEN_TAGS = new Set(HTML_POLICY_FORBIDDEN_TAGS);
const URI_ATTRIBUTES = new Set(['href', 'src']);
const SAFE_URI_PATTERN = /^(?:(?:https?|mailto|tel):|#|\/|\.\/|\.\.\/|data:image\/(?:png|gif|jpeg|jpg|webp|avif);base64,)/i;
const ALLOWED_TAGS = new Set(HTML_POLICY_ALLOWED_TAGS);
const ALLOWED_ATTRIBUTES = new Set(HTML_POLICY_ALLOWED_ATTRIBUTES);
const ALLOWED_STYLES = new Set(HTML_POLICY_ALLOWED_STYLE_PROPERTIES);
export const isSafeRuntimeUri = (value) => SAFE_URI_PATTERN.test(value.trim()) && !/javascript:/i.test(value);
export const sanitizeRuntimeStyle = (value) => value
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
    const separator = declaration.indexOf(':');
    if (separator <= 0)
        return '';
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const rawValue = declaration.slice(separator + 1).trim();
    if (!ALLOWED_STYLES.has(property))
        return '';
    if (/[<>{}\\]/.test(rawValue))
        return '';
    if (/(?:url|expression|javascript|@import|behavior)\s*\(/i.test(rawValue))
        return '';
    if (!/^[#%(),.\-+/ 0-9a-zA-Z]+$/.test(rawValue))
        return '';
    return `${property}:${rawValue}`;
})
    .filter(Boolean)
    .join(';');
const escapeHtml = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
const escapeAttribute = (value) => escapeHtml(value)
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
const sanitizeAttributeValue = (name, value) => {
    if (name === 'style')
        return sanitizeRuntimeStyle(value);
    if (URI_ATTRIBUTES.has(name.toLowerCase()) && !isSafeRuntimeUri(value))
        return '';
    if (/javascript:/i.test(value))
        return '';
    return value;
};
const sanitizeAttributes = (source) => {
    const attributes = [];
    const pattern = /([:\w-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
    let match;
    while ((match = pattern.exec(source))) {
        const name = (match[1] ?? '').trim();
        if (!name || name.toLowerCase().startsWith('on') || !ALLOWED_ATTRIBUTES.has(name))
            continue;
        const rawValue = match[2] ?? match[3] ?? match[4] ?? '';
        const value = sanitizeAttributeValue(name, rawValue);
        if (!value)
            continue;
        attributes.push(`${name}="${escapeAttribute(value)}"`);
    }
    return attributes.length ? ` ${attributes.join(' ')}` : '';
};
const stripForbiddenBlocks = (value) => {
    let sanitized = value;
    for (const tag of FORBIDDEN_TAGS) {
        sanitized = sanitized.replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi'), '');
    }
    return sanitized;
};
export const sanitizeRuntimeHtml = (value, options = {}) => {
    if (typeof value !== 'string')
        return '';
    const maxOutputCharacters = options.maxOutputCharacters ?? HTML_POLICY_MAX_OUTPUT_CHARACTERS;
    const bounded = value.slice(0, Math.max(0, maxOutputCharacters));
    return stripForbiddenBlocks(bounded)
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<\/?([A-Za-z][\w:-]*)([^>]*)>/g, (fullTag, rawTagName, rawAttributes) => {
        const tagName = rawTagName.trim().toLowerCase();
        if (FORBIDDEN_TAGS.has(tagName) || !ALLOWED_TAGS.has(tagName))
            return '';
        if (fullTag.startsWith('</'))
            return VOID_TAGS.has(tagName) ? '' : `</${tagName}>`;
        return `<${tagName}${sanitizeAttributes(rawAttributes)}>`;
    })
        .replace(/<(?!\/?(?:[A-Za-z][\w:-]*)(?:\s|>|\/))/g, '&lt;');
};
